import { createFileRoute } from "@tanstack/react-router";
import type {
  ClientMasterPlan,
  ClientMasterPlanHydratedWithPublishedDate,
} from "api-types/masterPlans/masterPlansResponses";
import { ensureDefined } from "common/utils/assertOfType";
import Toggle from "lib/components/actions/Toggle";
import BuilderPortalEmptyCTA from "lib/components/builders/BuilderPortalEmptyCTA";
import BuilderPortalLayout from "lib/components/builders/BuilderPortalLayout";
import WarnOutOfDate from "lib/components/builders/communities/plans/homePlans/WarnOutOfDate";
import MasterPlanOverflowMenu from "lib/components/builders/communities/plans/MasterPlanOverflowMenu";
import BuilderBulkAddPlansToLocationsModal from "lib/components/builders/communities/plans/modals/BuilderBulkAddPlansToLocationsModal";
import PlanList from "lib/components/builders/communities/plans/PlanList";
import planListStyles from "lib/components/builders/communities/plans/PlanList.module.scss";
import BuilderAddMasterPlanModal from "lib/components/builders/files/BuilderAddMasterPlanModal";
import MasterPlanCard from "lib/components/builders/plan_cards/MasterPlanCard";
import PlanThumbnailCell from "lib/components/builders/PlanThumbnailCell";
import { getBuilderMasterPlansURL } from "lib/endpointHelpers";
import { useFetch } from "lib/fetcher";
import { getPlanStat, hasPlanStat } from "lib/house/stats/planStatsLookup";
import { getPlanStartingPriceString } from "lib/options/pricing";
import { masterPlanLibraryPath, masterPlanShowroomPath, useAppRouterParams } from "lib/routeHelpers";
import { useUserContext } from "lib/state/UserContextProvider";
import { deleteMasterPlan } from "lib/storage/masterPlanStorage";
import { useEffect, useMemo, useState } from "react";
import Button from "ui/buttons/Button";
import useAppContext from "ui/hooks/useAppContext";
import useNotificationQueryParams from "ui/hooks/useNotificationQueryParams";
import IconGlobeHemisphereWest from "ui/icons/IconGlobeHemisphereWest";
import IconPlus from "ui/icons/IconPlus";
import type { Column } from "ui/tables/DataTable/types";
import withAuthentication from "../../../../components/withAuthentication";
import styles from "./index.module.scss";

function PlansPage() {
  const [detached, setDetached] = useState<boolean>(false);
  const [showArchived, setShowArchived] = useState<boolean>(false);
  const [showAddMasterPlanModal, setShowAddMasterPlanModal] = useState(false);
  const [showBulkAddModal, setShowBulkAddModal] = useState(false);
  const { builderId } = useAppRouterParams();
  const {
    data: masterPlans,
    loading: masterPlansLoading,
    mutate,
  } = useFetch<ClientMasterPlanHydratedWithPublishedDate[]>(
    builderId ? getBuilderMasterPlansURL({ builderId, detached, includeArchived: showArchived }) : "",
    [builderId],
  );

  const { message, messageType, timeout } = useNotificationQueryParams(["message", "messageType", "timeout"]);
  const { setUserNotification } = useAppContext();
  const { hasPermission } = useUserContext();

  useEffect(() => {
    if (masterPlansLoading) {
      return;
    }
    if (message) {
      setUserNotification({
        message,
        messageType,
        timeout,
      });
    }
  }, [masterPlansLoading, message]);

  const handleAddMasterPlanSuccess = (_masterPlan: ClientMasterPlan) => {
    void mutate();
  };

  const tableColumns: Column<ClientMasterPlanHydratedWithPublishedDate>[] = useMemo(
    () => [
      {
        cell: function ThumbnailCell({ row }) {
          return <PlanThumbnailCell plan={row.original} className={planListStyles.rowThumbnail} />;
        },
        enableResizing: false,
        size: 150,
        fullCell: true,
        id: "thumbnailUrl",
      },
      {
        header: "Plan Name",
        accessor: function NameCell(plan) {
          return <span className={planListStyles.tableRowTitle}>{plan.name}</span>;
        },
        width: 0.4,
      },
      {
        header: "Base Price",
        accessor: function BasePriceCell(plan) {
          return <span>{getPlanStartingPriceString(plan)}</span>;
        },
        sortType: (rowA, rowB, id, desc) => {
          const priceA = rowA.values[id].props.children;
          const priceB = rowB.values[id].props.children;
          const priceAValue = priceA === "TBD" ? 0 : parseInt(priceA.replace(/[^0-9]/g, ""));
          const priceBValue = priceB === "TBD" ? 0 : parseInt(priceB.replace(/[^0-9]/g, ""));

          if (desc) {
            return priceAValue < priceBValue ? 1 : -1;
          } else {
            return priceBValue > priceAValue ? 1 : -1;
          }
        },
      },
      {
        header: "SQFT",
        cell: function TotalFloorAreaCell({ row }) {
          const plan = row.original;
          let floorArea: string;
          let usesFallback = false;
          if (hasPlanStat(plan.stats, "conditionedFloorArea")) {
            floorArea = getPlanStat(plan.stats, "conditionedFloorArea");
          } else {
            floorArea = getPlanStat(plan.stats, "totalFloorArea");
            usesFallback = true;
          }

          return (
            <span>
              {floorArea} SQFT
              {usesFallback && <WarnOutOfDate />}
            </span>
          );
        },
        accessor: (plan) => {
          let floorArea: string;
          if (hasPlanStat(plan.stats, "conditionedFloorArea")) {
            floorArea = getPlanStat(plan.stats, "conditionedFloorArea");
          } else {
            floorArea = getPlanStat(plan.stats, "totalFloorArea");
          }
          return parseInt(floorArea.replace(/,/g, ""));
        },
        type: "NUMBER",
      },
      {
        header: "Published",
        accessor: (plan) => plan.publishedAt,
        type: "DATETIME",
      },
      ...(showArchived
        ? [
            {
              header: "Archived",
              accessor: (plan: ClientMasterPlanHydratedWithPublishedDate) => plan.archivedAt ?? null,
              type: "DATETIME" as const,
            },
          ]
        : []),
    ],
    [showArchived],
  );

  const handleDeleteMasterPlan = async (masterPlan: ClientMasterPlanHydratedWithPublishedDate) => {
    try {
      await deleteMasterPlan(masterPlan.builderId ?? undefined, masterPlan.id);
      await mutate((prev) => prev?.filter(({ id }) => id !== masterPlan.id));
      setUserNotification({ messageType: "success", message: "Master Plan deleted successfully." });
    } catch (error) {
      console.error(error);
      setUserNotification({ messageType: "error", message: "An error occurred while deleting this Master Plan." });
    }
  };

  const masterPlanLink = (masterPlan: ClientMasterPlanHydratedWithPublishedDate) => {
    return hasPermission("EDIT_MASTER_PLANS")
      ? masterPlanLibraryPath(ensureDefined(builderId), masterPlan.id)
      : masterPlanShowroomPath(ensureDefined(masterPlan.builderId), masterPlan.id);
  };

  return (
    <BuilderPortalLayout
      header="Plan Library"
      headerActions={
        <div className={styles.headerActions}>
          <div>
            <Toggle isTrue={detached} onChange={setDetached} dataTestId="toggle-detached-plans" />
            <label>Detached Plans</label>
          </div>
          <div>
            <Toggle isTrue={showArchived} onChange={setShowArchived} dataTestId="toggle-archived-plans" />
            <label>Include Archived Plans</label>
          </div>
        </div>
      }
      selected="plans"
      pageTitle="Plans"
      loadingContent={masterPlansLoading}
    >
      <div className="builder-portal-layout-content">
        {!!masterPlans?.length ? (
          <PlanList
            planTableColumns={tableColumns}
            overflowMenu={(row) => <MasterPlanOverflowMenu masterPlan={row.original} onUpdate={mutate} />}
            plans={masterPlans.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""))}
            getRowHref={(row) => masterPlanLink(row.original)}
            renderGridCard={(masterPlan) => (
              <MasterPlanCard
                key={masterPlan.name}
                selected={false}
                masterPlan={masterPlan}
                href={masterPlanLink(masterPlan)}
                onUpdate={mutate}
                onDelete={() => handleDeleteMasterPlan(masterPlan)}
                context="MARKETING"
              />
            )}
            sectionHeaderProps={{
              title: "Master Plans",
              headerButton: hasPermission("EDIT_MASTER_PLANS") ? (
                <div className={styles.actionButtonGroup}>
                  <Button btnSize="small" btnStyle="quaternary" onClick={() => setShowBulkAddModal(true)}>
                    <IconGlobeHemisphereWest />
                    Release Plans
                  </Button>
                  <Button btnSize="small" btnStyle="primary" onClick={() => setShowAddMasterPlanModal(true)}>
                    <IconPlus />
                    Master Plan
                  </Button>
                </div>
              ) : null,
            }}
          />
        ) : (
          !masterPlansLoading && (
            <div className={styles.emptyStateContainer}>
              <BuilderPortalEmptyCTA>
                <div className="cta-container">
                  <div className="cta-title">No Master Plans</div>
                  <div className="cta-text">
                    {hasPermission("EDIT_MASTER_PLANS") ? (
                      <>
                        <p>Create a Master Plan from any published home in your Studio Files.</p>
                        <Button onClick={() => setShowAddMasterPlanModal(true)}>Add Master Plan</Button>
                      </>
                    ) : (
                      "To create a Master Plan, go to your Studio Files tab and select Create Master Plan from the menu of any published home."
                    )}
                  </div>
                </div>
              </BuilderPortalEmptyCTA>
            </div>
          )
        )}
      </div>

      {showAddMasterPlanModal && (
        <BuilderAddMasterPlanModal
          builderId={ensureDefined(builderId)}
          onClose={() => setShowAddMasterPlanModal(false)}
          onSuccess={handleAddMasterPlanSuccess}
        />
      )}

      {showBulkAddModal && (
        <BuilderBulkAddPlansToLocationsModal
          builderId={ensureDefined(builderId)}
          masterPlans={masterPlans ?? []}
          onClose={() => setShowBulkAddModal(false)}
        />
      )}
    </BuilderPortalLayout>
  );
}

export const Route = createFileRoute("/builders/$builder_id/master-plans/")({
  component: withAuthentication(PlansPage, { permissions: ["VIEW_MASTER_PLANS"] }),
});
